import { inject, Injectable } from '@angular/core';
import {
  type CreateLearningResourceMicroLearningStepInput,
  type CreateLearningResourceMicroLearningStepMutation,
  type CreateLearningResourceMicroLearningStepMutationVariables,
  type DestroyLearningResourceMicroLearningStepInput,
  type DestroyLearningResourceMicroLearningStepMutation,
  type DestroyLearningResourceMicroLearningStepMutationVariables,
  type LearningResourceMicroLearningStepQuery,
  type LearningResourceMicroLearningStepQueryVariables,
  type UpdateLearningResourceMicroLearningStepInput,
  type UpdateLearningResourceMicroLearningStepMutation,
  type UpdateLearningResourceMicroLearningStepMutationVariables,
} from '@reconcept-platform/shared/types';
import { gql } from 'apollo-angular';
import { map } from 'rxjs';
import { type BatchMutationVariables } from '../../../services/graphql.service';
import { SharedGraphQlService } from '../../../services/shared-graphql.service';
import { assertGraphqlDataNode } from '../../../utils/type-helpers';
import {
  type LearningResourceMicroLearningId,
  type LearningResourceMicroLearningStepId,
} from '../../../utils/type-helpers-ids.generated';
import { attachedFileFragment } from '../../upload/upload.fragments';
import { batchUpdateLearningResourceMicrolearningStep } from './../learning-resource-micro-learning-step.batch-mutation';
import { learningResourceMicrolearningQuery } from './shared-learning-resource-micro-learning.api';

const learningResourceMicrolearningStepQuery = gql`
  query LearningResourceMicroLearningStep($learningResourceMicroLearningStepId: ID!) {
    node(id: $learningResourceMicroLearningStepId) {
      ... on LearningResourceMicroLearningStep {
        id
        title
        descriptionJsonContent
        image {
          ...AttachedFile
        }
        link
        linkButtonText
        order
      }
    }
  }
  ${attachedFileFragment}
`;

@Injectable({
  providedIn: 'root',
})
export class SharedLearningResourceMicrolearningStepApi {
  private readonly sharedGraphQlService = inject(SharedGraphQlService);

  watchLearningResourceMicrolearningStep(learningResourceMicroLearningStepId: LearningResourceMicroLearningStepId) {
    return this.sharedGraphQlService
      .watchQuery<LearningResourceMicroLearningStepQuery, LearningResourceMicroLearningStepQueryVariables>({
        query: learningResourceMicrolearningStepQuery,
        variables: {
          learningResourceMicroLearningStepId: learningResourceMicroLearningStepId.id,
        },
      })
      .pipe(map((result) => assertGraphqlDataNode(result, 'LearningResourceMicroLearningStep')));
  }

  createLearningResourceMicrolearningStep(input: CreateLearningResourceMicroLearningStepInput) {
    return (
      this.sharedGraphQlService
        .mutateV2<
          CreateLearningResourceMicroLearningStepMutation,
          CreateLearningResourceMicroLearningStepMutationVariables
        >({
          mutation: gql`
            mutation CreateLearningResourceMicroLearningStep($input: CreateLearningResourceMicroLearningStepInput!) {
              createLearningResourceMicroLearningStep(input: $input) {
                errors {
                  message
                  path
                }
                learningResourceMicroLearningStep {
                  id
                  title
                  descriptionJsonContent
                  image {
                    ...AttachedFile
                  }
                  link
                  linkButtonText
                  order
                  microLearning {
                    id
                  }
                }
              }
            }
            ${attachedFileFragment}
          `,
          variables: { input },
          refetchQueries: [
            {
              query: learningResourceMicrolearningQuery,
              variables: { learningResourceMicroLearningId: input.learningResourceMicroLearningId },
            },
          ],
        })
        // @ts-expect-error TS18048 possibly undefined | TS18049 possibly null or undefined
        .pipe(map((result) => result.data.createLearningResourceMicroLearningStep.learningResourceMicroLearningStep))
    );
  }

  updateLearningResourceMicrolearningStep(input: UpdateLearningResourceMicroLearningStepInput) {
    return (
      this.sharedGraphQlService
        .mutateV2<
          UpdateLearningResourceMicroLearningStepMutation,
          UpdateLearningResourceMicroLearningStepMutationVariables
        >({
          mutation: gql`
            mutation UpdateLearningResourceMicroLearningStep($input: UpdateLearningResourceMicroLearningStepInput!) {
              updateLearningResourceMicroLearningStep(input: $input) {
                errors {
                  message
                  path
                }
                learningResourceMicroLearningStep {
                  id
                  title
                  descriptionJsonContent
                  image {
                    ...AttachedFile
                  }
                  link
                  linkButtonText
                  order
                }
              }
            }
            ${attachedFileFragment}
          `,
          variables: { input },
        })
        // @ts-expect-error TS18048 possibly undefined | TS18049 possibly null or undefined
        .pipe(map((result) => result.data.updateLearningResourceMicroLearningStep.learningResourceMicroLearningStep))
    );
  }

  updateMicroLearningSlidesOrder(slides: LearningResourceMicroLearningStepId[]) {
    const toUpdate: UpdateLearningResourceMicroLearningStepInput[] = slides.map((slide, index) => ({
      learningResourceMicroLearningStepId: slide.id,
      order: index,
    }));
    const mutations: string[] = [];
    const variables: { [variableKey: string]: BatchMutationVariables }[] = [];

    toUpdate.forEach((input) => {
      mutations.push(batchUpdateLearningResourceMicrolearningStep);
      variables.push({
        learningResourceMicroLearningStepInput: {
          type: 'UpdateLearningResourceMicroLearningStepInput!',
          value: input,
        },
      });
    });

    return this.sharedGraphQlService.mutateBatch({
      mutations,
      variables,
    });
  }

  destroyLearningResourceMicroLearningStep(
    input: DestroyLearningResourceMicroLearningStepInput,
    learningResourceMicroLearningId: LearningResourceMicroLearningId,
  ) {
    return this.sharedGraphQlService.mutateV2<
      DestroyLearningResourceMicroLearningStepMutation,
      DestroyLearningResourceMicroLearningStepMutationVariables
    >({
      mutation: gql`
        mutation DestroyLearningResourceMicroLearningStep($input: DestroyLearningResourceMicroLearningStepInput!) {
          destroyLearningResourceMicroLearningStep(input: $input) {
            errors {
              message
              path
            }
          }
        }
      `,
      variables: {
        input,
      },
      refetchQueries: [
        {
          query: learningResourceMicrolearningQuery,
          variables: { learningResourceMicroLearningId: learningResourceMicroLearningId.id },
        },
      ],
    });
  }
}
